import * as React from "react";
import { withRouter, RouteComponentProps } from "react-router";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import { URLS } from "@/utils";
import { Button, Card, Field, Input } from "@/components/ui";
import { Png } from "@/assets";
import { SocialCoachSessionService } from "@/services";
import { VerificationActions } from "@/interfaces";
import { ArrowLeft } from "lucide-react";

const resendSchema = z.object({
  email: z.email().trim().min(1, "Email is Required"),
});

type ResendValues = z.infer<typeof resendSchema>;

interface OwnProps {
  type?: VerificationActions;
}

type Props = OwnProps & RouteComponentProps;

const ResendPageFC: React.FC<Props> = ({ history, type = "ResendVerify" }) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

  const form = useForm<ResendValues>({
    resolver: zodResolver(resendSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (data: ResendValues) => {
    setIsSubmitting(true);
    try {
      if (type === "ForgotPassword") {
        await SocialCoachSessionService.resetPasswordRequest(data.email);
      }
      setSuccessMessage("If an account exists for this email, you will receive instructions shortly.");
      setTimeout(() => {
        history.push(URLS.login);
      }, 5000);
    } catch (e) {
      setSuccessMessage("If an account exists for this email, you will receive instructions shortly.");
      setTimeout(() => {
        history.push(URLS.login);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toLogin = (): void => {
    history.push(URLS.login);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-120px)] sm:min-h-[calc(100vh-200px)] p-4 sm:p-6">
      <Card className="w-full max-w-[550px] p-6 sm:p-10 shadow-lg border-slate-200">
        <div className="flex flex-col mb-6 gap-2">
          <div className="flex justify-center">
            <img src={Png.Logo} alt="SocialCoach" className="w-48 sm:w-60" />
          </div>
          <p className="text-slate-600 text-center font-semibold text-lg">
            {type === "ForgotPassword" ? "Forgot your password?" : "Resend verification email"}
          </p>
        </div>

        {successMessage ? (
          <div className="p-4 bg-green-50 border border-green-200 text-green-700 text-center rounded-md">
            {successMessage}
          </div>
        ) : (
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <Controller
              name="email"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field label="Email Address" error={fieldState.error?.message}>
                  <Input {...field} type="email" placeholder="email@example.com" disabled={isSubmitting} />
                </Field>
              )}
            />

            <Button type="submit" size="lg" className="w-full h-11 sm:h-12 text-base mt-2" loading={isSubmitting}>
              {type === "ForgotPassword" ? "Reset Password" : "Resend Email"}
            </Button>

            <div className="flex justify-center items-center py-4">
              <button
                type="button"
                onClick={toLogin}
                className="text-sm text-primary hover:text-slate-600 tracking-wide cursor-pointer font-sans flex flex-row items-center gap-2"
              >
                <ArrowLeft size={15} />
                Back to login
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
};

export const ResendPage = withRouter(ResendPageFC);
